import type { ITransformRT2, IVector2 } from '@seddi/math-v2';
import { type PieceLogo, type SceneEntity } from '@seddi/scene';
import { LogoTechnique, PieceSide } from '@seddi/scene';
import { SurfaceMaterialModel, TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import type { LogoDesc, SceneDesc } from '@seddi/ui-three-viewer';
import { produce } from 'immer';

import type { StyleLogoRegion } from '../api/styleAggregator';
import {
  type MockupLogo,
  logoTextureImageSrc,
  normalizeRecoloredImageSrc,
} from '../types/mockupLogos';
import { isInside as isLogoInside } from './logoBounds';
import { getDiffLogos } from './logosDiff';
import {
  calculateHoverTextureSize,
  createHoverTexture,
  createLogoRegionTexture,
  regionSizeToLineSizes,
  regionTextureSizeNumbers,
} from './viewerLogoRegionTexture';

export type LogoRegion = StyleLogoRegion & { entityId: string };

type Logo = { piece: Readonly<PieceLogo>; resource: Readonly<LogoDesc> };

type Logos = { isDirty: boolean; scene: Record<string, Logo>; state: Readonly<MockupLogo[]> };

type Hover = {
  isDirty: boolean;
  useSelectedColor: boolean;
  scene: { piece?: PieceLogo; resource?: LogoDesc };
  state?: Readonly<MockupLogo>;
};

type StyleRegion = { resource: Readonly<LogoDesc>; resourceWarn: Readonly<LogoDesc> };

type StyleRegions = {
  isDirty: boolean;
  scene: Record<string, StyleRegion>;
  state: Readonly<LogoRegion[]>;
};

type Selection = { isDirty: boolean; scene?: PieceLogo; state?: Readonly<MockupLogo> };

type StyleScene = { isDirty: boolean; state?: Readonly<SceneDesc> };

export type ViewerScene = {
  readonly hover: Hover;
  readonly logos: Logos;
  readonly selection: Selection;
  readonly styleRegions: StyleRegions;
  readonly styleScene: StyleScene;
  scene?: Readonly<SceneDesc>;
};

/**
 * Create new scene context.
 */
export const create = (): ViewerScene => {
  return {
    hover: {
      isDirty: false,
      useSelectedColor: false,
      scene: {},
      state: undefined,
    },
    logos: { isDirty: false, scene: {}, state: [] },
    selection: { isDirty: false },
    styleRegions: { isDirty: false, scene: {}, state: [] },
    styleScene: { isDirty: false },
  };
};

/**
 * Set style regions (with entityId for each region).
 */
export const setStyleRegions = (self: ViewerScene, state: Readonly<LogoRegion[]>) => {
  self.styleRegions.isDirty = true;
  self.styleRegions.state = state;
  self.logos.isDirty = true;
  self.logos.state.forEach(l => updateLogoRegion(self, l.id, l.region));
};

/**
 * Reconcile selection (draw region border around selected logo).
 */
export const reconcileSelection = async (self: ViewerScene, next: MockupLogo | undefined) => {
  const prev = self.selection.state;
  if (prev === next) return;

  if (!next) {
    clearSelection(self);
  } else if (!prev || prev.id !== next.id) {
    await createSelection(self, next);
  } else {
    if (prev.region !== next.region) {
      await createSelection(self, next);
    } else {
      updateSelectionResource(self, next);
    }
  }

  self.selection.isDirty = true;
  self.selection.state = next;
};

/**
 * Reconcile hover (draw logo border when hovering or when selected).
 * @param next Logo to show border around (hover or selected).
 * @param useSelectedColor Use selected color (darker) instead of hover color.
 */
export const reconcileHover = async (
  self: ViewerScene,
  next: MockupLogo | undefined,
  useSelectedColor = false,
) => {
  const prev = self.hover.state;
  const prevUseSelectedColor = self.hover.useSelectedColor;

  if (isMockupLogoEqual(prev, next) && prevUseSelectedColor === useSelectedColor) {
    return;
  }

  if (!next) {
    clearHover(self);
  } else if ((!prev && next) || prev!.id !== next.id || prevUseSelectedColor !== useSelectedColor) {
    await createHover(self, next, useSelectedColor);
  } else if (prev!.region !== next.region) {
    await createHover(self, next, useSelectedColor);
  } else {
    if (!isVector2Equal(prev!.size, next.size)) {
      await updateHoverSize(self, next.size);
    }
    if (!isTransformEqual(prev!.transform, next.transform)) {
      updateHoverTransform(self, next.transform);
    }
  }

  self.hover.isDirty = true;
  self.hover.useSelectedColor = useSelectedColor;
  self.hover.state = next;
};

/**
 * Set style scene (base scene without logos).
 */
export const setStyleScene = (
  self: ViewerScene,
  state: Readonly<SceneDesc>,
  hideTearAwayLabel: boolean,
) => {
  self.styleScene.isDirty = true;

  if (hideTearAwayLabel) {
    const transformedState = produce(state, scene => {
      scene.children.forEach(child => {
        if (child.components?.garmentPiece?.logos) {
          child.components.garmentPiece.logos = child.components.garmentPiece.logos.filter(
            (logo: any) => !logo.isTearAwayLabel,
          );
        }
      });
    });
    self.styleScene.state = transformedState;
  } else {
    self.styleScene.state = state;
  }
};

/**
 * Reconcile logos using diff-based updates.
 */
export const reconcileLogos = (self: ViewerScene, next: MockupLogo[]) => {
  const prev = self.logos.state;
  if (isMockupLogoArrayEqual(prev, next)) {
    return;
  }

  const diff = getDiffLogos(prev, next);

  for (const tuple of diff) {
    reconcileLogo(self, tuple[0], tuple[1]);
  }

  self.logos.isDirty = true;
  self.logos.state = next;
};

/**
 * Update scene descriptor by composing base scene with logos and selection.
 */
export const updateScene = (self: ViewerScene) => {
  const { hover, logos, selection, styleScene } = self;

  if (!hover.isDirty && !logos.isDirty && !selection.isDirty && !styleScene.isDirty) {
    return;
  }

  styleScene.isDirty = false;

  if (!styleScene.state) {
    self.scene = undefined;
    return;
  }
  self.scene = styleScene.state;

  const { styleRegions } = self;
  const logosState = logos.state;

  for (const logoState of logosState) {
    const regionState = styleRegions.state.find(r => r.id === logoState.region);
    if (!regionState) {
      continue;
    }
    const logoScene = logos.scene[logoState.id];
    const entityId = regionState.entityId;

    if (!logoScene || !entityId) {
      continue;
    }

    self.scene = produce(self.scene!, scene => {
      const entity = scene.children.find((e: any) => e._id === entityId);
      if (!entity) {
        return;
      }
      pushLogo(entity, logoScene.piece);
      (scene as any).logos ??= [];
      (scene as any).logos.push(logoScene.resource);
    });
  }

  logos.isDirty = false;

  // Hover reconciliation: draw logo border when hovering.
  if (hover.state && hover.scene.piece && hover.scene.resource) {
    const regionState = styleRegions.state.find(r => r.id === hover.state!.region);
    if (regionState?.entityId) {
      self.scene = produce(self.scene!, scene => {
        const entity = scene.children.find((e: any) => e._id === regionState.entityId);
        if (!entity) return;
        pushLogo(entity, hover.scene.piece!);
        (scene as any).logos ??= [];
        (scene as any).logos.push(hover.scene.resource!);
      });
    }
  }
  hover.isDirty = false;

  // Selection reconciliation: draw style region border around selected logo.
  if (selection.state) {
    const regionState = styleRegions.state.find(r => r.id === selection.state!.region);
    const regionScene = styleRegions.scene[selection.state.region];
    if (regionState && regionScene && selection.scene) {
      const entityId = regionState.entityId;
      if (entityId) {
        self.scene = produce(self.scene!, scene => {
          const entity = scene.children.find((e: any) => e._id === entityId);
          if (!entity) {
            return;
          }
          pushLogo(entity, selection.scene!);
          (scene as any).logos ??= [];
          (scene as any).logos.push(regionScene.resource, regionScene.resourceWarn);
        });
      }
    }
  }
  selection.isDirty = false;
};

const createLogoResource = (
  id: string,
  imageSrc: string,
  size: { x: number; y: number },
): LogoDesc => {
  return {
    id,
    materials: [
      {
        id,
        model: SurfaceMaterialModel.DISNEY,
        params: {
          baseColorMap: {
            id,
            href: imageSrc,
            colorSpace: TextureColorSpace.GAMMA,
            format: TextureFormat.PNG,
          },
          roughness: 0.3,
          specular: 0.8,
        },
      },
    ],
    name: id,
    size,
    _id: id,
    image: imageSrc,
  } as LogoDesc;
};

const clearSelection = (self: ViewerScene) => {
  self.selection.scene = undefined;
  self.selection.state = undefined;
};

const clearHover = (self: ViewerScene) => {
  self.hover.scene.piece = undefined;
  self.hover.scene.resource = undefined;
  self.hover.state = undefined;
};

const createLogoBorderTexture = async (
  logoSize: IVector2,
  useSelectedColor: boolean,
): Promise<LogoDesc> => {
  const id = crypto.randomUUID();
  const imageBitmap = await createHoverTexture(logoSize, useSelectedColor);
  const size = calculateHoverTextureSize(logoSize);
  return {
    id,
    materials: [
      {
        id,
        model: SurfaceMaterialModel.DISNEY,
        params: {
          baseColorMap: {
            id,
            imageBitmap,
            colorSpace: TextureColorSpace.GAMMA,
          },
          roughness: 0.2,
          specular: 0.2,
        },
      },
    ],
    name: id,
    size,
    _id: id,
  } as LogoDesc;
};

const createHover = async (self: ViewerScene, logo: MockupLogo, useSelectedColor: boolean) => {
  const regionState = self.styleRegions.state.find(r => r.id === logo.region);
  if (!regionState) return;

  const resource = await createLogoBorderTexture(logo.size as IVector2, useSelectedColor);

  self.hover.scene.resource = resource;
  self.hover.scene.piece = {
    _id: `${logo.id}:hover`,
    meshId: regionState.pieceName,
    resource: resource.id,
    size: calculateHoverTextureSize(logo.size as IVector2),
    side: (regionState.side as any) || PieceSide.FRONT,
    technique: LogoTechnique.Rubber,
    transform: logo.transform as ITransformRT2,
    pivot: { x: 0, y: 0 },
    name: regionState.name,
  } as PieceLogo;
};

const updateHoverSize = async (self: ViewerScene, size: IVector2) => {
  const prev = self.hover.state;
  if (!prev || !self.hover.scene.piece) return;
  const useSelectedColor = self.hover.useSelectedColor;
  const resource = await createLogoBorderTexture(size, useSelectedColor);
  self.hover.scene.resource = resource;
  self.hover.scene.piece = {
    ...self.hover.scene.piece,
    size: calculateHoverTextureSize(size),
    resource: resource.id,
  };
};

const updateHoverTransform = (self: ViewerScene, transform: ITransformRT2) => {
  if (!self.hover.scene.piece) return;
  self.hover.scene.piece = { ...self.hover.scene.piece, transform };
};

const createRegion = async (self: ViewerScene, region: LogoRegion) => {
  const { id, size } = region;

  const [imageBitmap, imageBitmap2] = await Promise.all([
    createLogoRegionTexture(size as IVector2, 'black', 'white'),
    createLogoRegionTexture(size as IVector2, 'black', 'red'),
  ]);

  const resourceId = crypto.randomUUID();
  const resourceId2 = crypto.randomUUID();

  self.styleRegions.scene[id] = {
    resource: createRegionResource(resourceId, imageBitmap, size as IVector2),
    resourceWarn: createRegionResource(resourceId2, imageBitmap2, size as IVector2),
  };
};

const createRegionResource = (id: string, imageBitmap: ImageBitmap, size: IVector2): LogoDesc => {
  const lineSizes = regionSizeToLineSizes(size);
  const regionSize = regionTextureSizeNumbers(size, lineSizes.lineWidth).metersSize;
  return {
    id,
    image: 'unused',
    materials: [
      {
        id,
        model: SurfaceMaterialModel.DISNEY,
        params: {
          baseColorMap: {
            id,
            imageBitmap,
            colorSpace: TextureColorSpace.GAMMA,
          },
          roughness: 1.0,
          specular: 0.5,
        },
      },
    ],
    name: id,
    size: { ...regionSize },
    _id: id,
  } as LogoDesc;
};

const createSelection = async (self: ViewerScene, logo: MockupLogo) => {
  const region = logo.region;
  const regionState = self.styleRegions.state.find(r => r.id === region);
  if (!regionState) return;

  let regionScene = self.styleRegions.scene[region];
  if (!regionScene) {
    await createRegion(self, regionState);
    regionScene = self.styleRegions.scene[region];
  }
  if (!regionScene) return;

  const isInside = isLogoInside(
    logo.size,
    logo.transform,
    regionState.size as IVector2,
    regionState.transform as ITransformRT2,
  );

  const resource = isInside ? regionScene.resource.id : regionScene.resourceWarn.id;
  const lineSizes = regionSizeToLineSizes(regionState.size as IVector2);
  const size = regionTextureSizeNumbers(
    regionState.size as IVector2,
    lineSizes.lineWidth,
  ).metersSize;

  self.selection.scene = {
    _id: crypto.randomUUID(),
    meshId: regionState.pieceName,
    resource,
    size,
    side: (regionState.side as any) || PieceSide.FRONT,
    technique: LogoTechnique.Rubber,
    transform: regionState.transform as ITransformRT2,
    pivot: { x: 0, y: 0 },
    name: regionState.name,
  } as PieceLogo;
};

const updateSelectionResource = (self: ViewerScene, next: MockupLogo) => {
  const regionState = self.styleRegions.state.find(r => r.id === next.region);
  const regionScene = self.styleRegions.scene[next.region];
  if (!regionState || !regionScene || !self.selection.scene) return;

  const isInside = isLogoInside(
    next.size,
    next.transform,
    regionState.size as IVector2,
    regionState.transform as ITransformRT2,
  );

  const resourceId = isInside ? regionScene.resource.id : regionScene.resourceWarn.id;

  if (self.selection.scene.resource !== resourceId) {
    self.selection.scene = {
      ...self.selection.scene,
      resource: resourceId,
    };
  }
};

const pushLogo = (entity: SceneEntity, logo: PieceLogo) => {
  if (entity.components?.garmentPiece) {
    entity.components.garmentPiece.logos = entity.components.garmentPiece.logos ?? [];
    entity.components.garmentPiece.logos.push(logo);
  }
  if ((entity.components as any)?.model) {
    (entity.components as any).model.logos = (entity.components as any).model.logos ?? [];
    (entity.components as any).model.logos.push(logo);
  }
};

const clearLogo = (self: ViewerScene, id: string) => {
  const logo = self.logos.state.find(l => l.id === id);
  if (logo?.recoloredId) {
    delete self.logos.scene[id];
    delete self.logos.scene[logo.recoloredId];
  } else {
    delete self.logos.scene[id];
  }
};

const createLogo = (self: ViewerScene, logo: MockupLogo) => {
  const id = logo.id;

  const imageSrcToUse = logoTextureImageSrc(logo);
  if (!imageSrcToUse) {
    return;
  }

  const regionState = self.styleRegions.state.find(r => r.id === logo.region);
  if (!regionState) {
    return;
  }

  const recolorUrl = normalizeRecoloredImageSrc(logo.recoloredImageSrc);
  const resourceId = recolorUrl && logo.recoloredId ? logo.recoloredId : crypto.randomUUID();
  const resource = createLogoResource(resourceId, imageSrcToUse, logo.size);

  const piece: PieceLogo = {
    _id: id,
    meshId: regionState.pieceName,
    resource: resourceId,
    size: logo.size,
    side: (regionState.side as any) || PieceSide.FRONT,
    technique: logo.technique,
    transform: logo.transform as any,
    pivot: { x: 0, y: 0 },
    name: logo.displayName ?? logo.name,
  } as PieceLogo;

  self.logos.scene[id] = { piece, resource };
};

const reconcileLogo = (
  self: ViewerScene,
  prev: MockupLogo | undefined,
  next: MockupLogo | undefined,
) => {
  if (!next) {
    if (prev) {
      clearLogo(self, prev.id);
    }
  } else if (!prev) {
    createLogo(self, next);
  } else {
    const imageChanged =
      prev.recoloredId !== next.recoloredId ||
      prev.imageSrc !== next.imageSrc ||
      logoTextureImageSrc(prev) !== logoTextureImageSrc(next);

    if (imageChanged) {
      clearLogo(self, prev.id);
      createLogo(self, next);
    } else {
      if (prev.region !== next.region) {
        updateLogoRegion(self, next.id, next.region);
      }
      if (!isVector2Equal(prev.size, next.size)) {
        updateLogoSize(self, next.id, next.size);
      }
      if (prev.technique !== next.technique) {
        updateLogoTechnique(self, next.id, next.technique);
      }
      if (!isTransformEqual(prev.transform, next.transform)) {
        updateLogoTransform(self, next.id, next.transform);
      }
    }
  }
};

const updateLogoRegion = (self: ViewerScene, id: string, region: string) => {
  const scene = self.logos.scene[id];
  const regionState = self.styleRegions.state.find(r => r.id === region);
  if (!scene || !regionState) return;

  (scene as { piece: PieceLogo }).piece = {
    ...scene.piece,
    meshId: regionState.pieceName,
    side: (regionState.side as any) || PieceSide.FRONT,
  } as PieceLogo;
};

const updateLogoSize = (self: ViewerScene, id: string, size: IVector2) => {
  const scene = self.logos.scene[id];
  if (!scene) return;
  scene.piece = { ...scene.piece, size };
};

const updateLogoTechnique = (self: ViewerScene, id: string, technique: MockupLogo['technique']) => {
  const scene = self.logos.scene[id];
  if (!scene) return;
  scene.piece = { ...scene.piece, technique };
};

const updateLogoTransform = (self: ViewerScene, id: string, transform: ITransformRT2) => {
  const scene = self.logos.scene[id];
  if (!scene) return;
  scene.piece = { ...scene.piece, transform };
};

const isVector2Equal = (a: IVector2, b: IVector2): boolean => a.x === b.x && a.y === b.y;

const isTransformEqual = (a: ITransformRT2, b: ITransformRT2): boolean =>
  a.translation.x === b.translation.x &&
  a.translation.y === b.translation.y &&
  a.rotation === b.rotation;

const isMockupLogoEqual = (a: MockupLogo | undefined, b: MockupLogo | undefined): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.region === b.region &&
    a.technique === b.technique &&
    a.imageSrc === b.imageSrc &&
    normalizeRecoloredImageSrc(a.recoloredImageSrc) ===
      normalizeRecoloredImageSrc(b.recoloredImageSrc) &&
    a.recoloredId === b.recoloredId &&
    isVector2Equal(a.size, b.size) &&
    isTransformEqual(a.transform, b.transform)
  );
};

const isMockupLogoArrayEqual = (a: readonly MockupLogo[], b: readonly MockupLogo[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((logo, index) => isMockupLogoEqual(logo, b[index]));
};
